import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../config/api';

const BlogPostPage = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                console.log('📝 Fetching blog post:', slug);
                const response = await fetch(`${API_URL}/${slug}?_=${Date.now()}`);
                
                if (!response.ok) {
                    throw new Error('Post not found: ' + response.status);
                }
                
                const data = await response.json();
                console.log('✅ Blog post loaded:', data);
                setPost(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching blog post:', err);
                setError(err.message);
                setPost(null);
            } finally {
                setLoading(false);
            }
        };
        
        fetchPost();
    }, [slug]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        {error ? 'Hata: ' + error : 'Yazı bulunamadı'}
                    </h2>
                    <p className="text-gray-600 mb-4">
                        {error ? 'Bu blog yazısı veritabanında bulunamadı.' : 'Aradığınız blog yazısı bulunamadı.'}
                    </p>
                    <Link
                        to="/blog"
                        className="inline-flex items-center text-blue-600 hover:underline"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Blog sayfasına dön
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Header />
            <div className="container mx-auto px-4 py-12 max-w-4xl">
                <Link
                    to="/blog"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8 font-medium"
                >
                    <ArrowLeft size={20} className="mr-2" />
                    Blog'a dön
                </Link>

                <article className="bg-white rounded-xl shadow-lg overflow-hidden">
                    {post.image && (
                        <div className="h-64 md:h-96 overflow-hidden">
                            <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    <div className="p-8 md:p-12">
                        <div className="flex items-center text-gray-500 text-sm mb-6 pb-6 border-b border-gray-200">
                            <Calendar size={16} className="mr-2" />
                            {formatDate(post.created_at)}
                        </div>

                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 leading-tight">
                            {post.title}
                        </h1>

                        {/* Blog Content with better styling */}
                        <div className="prose prose-lg max-w-none">
                            <div 
                                className="text-gray-700 space-y-4 leading-relaxed
                                    [&>p]:mb-4 
                                    [&>p:last-child]:mb-0
                                    [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:text-gray-800 [&>h1]:mt-8 [&>h1]:mb-4
                                    [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-gray-800 [&>h2]:mt-6 [&>h2]:mb-3
                                    [&>h3]:text-lg [&>h3]:font-bold [&>h3]:text-gray-800 [&>h3]:mt-4 [&>h3]:mb-2
                                    [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-4
                                    [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-4
                                    [&>li]:mb-2
                                    [&>strong]:font-bold [&>strong]:text-gray-900
                                    [&>em]:italic
                                    [&>a]:text-blue-600 [&>a]:underline [&>a]:hover:text-blue-800
                                    [&>blockquote]:border-l-4 [&>blockquote]:border-blue-500 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-gray-600 [&>blockquote]:my-4
                                    [&>code]:bg-gray-100 [&>code]:px-2 [&>code]:py-1 [&>code]:rounded [&>code]:text-sm [&>code]:font-mono
                                    [&>pre]:bg-gray-100 [&>pre]:p-4 [&>pre]:rounded [&>pre]:overflow-x-auto [&>pre]:my-4"
                                dangerouslySetInnerHTML={{ __html: post.content }} 
                            />
                        </div>

                        {/* Meta tags if available */}
                        {(post.meta_keywords || post.meta_description) && (
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                {post.meta_keywords && (
                                    <div className="mb-3">
                                        <span className="text-sm font-semibold text-gray-600">Etiketler: </span>
                                        <span className="text-sm text-gray-600">{post.meta_keywords}</span>
                                    </div>
                                )}
                                {post.meta_description && (
                                    <div>
                                        <span className="text-sm font-semibold text-gray-600">Özet: </span>
                                        <span className="text-sm text-gray-600">{post.meta_description}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </article>

                <div className="text-center mt-8">
                    <Link
                        to="/blog"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Tüm yazıları görüntüle
                    </Link>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default BlogPostPage;
